import React, { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import TextField from "@material-ui/core/TextField";
import MenuItem from "@material-ui/core/MenuItem";
import Button from "@material-ui/core/Button";
import Typography from "@material-ui/core/Typography";
import Tooltip from "@material-ui/core/Tooltip";
import { makeStyles } from "@material-ui/core/styles";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";
import { formatPhone } from "../../utils/formatPhone";

const useStyles = makeStyles(theme => ({
  number: { fontWeight: 700, marginBottom: theme.spacing(2), fontVariantNumeric: "tabular-nums" },
  field: { marginBottom: theme.spacing(2.5) },
  hint: { color: theme.app.textTertiary, fontSize: 12, marginTop: -theme.spacing(1.5), marginBottom: theme.spacing(2) },
  // campo "Entrada" do número de teste quando o celular ainda não foi verificado:
  // no lugar do seletor de app, um link para verificar (com tooltip explicando).
  verifyField: {
    marginBottom: theme.spacing(2.5),
    border: `1px dashed ${theme.app.borderInput}`,
    borderRadius: 8,
    padding: "10px 12px",
  },
  verifyLabel: { fontSize: 11, color: theme.app.textTertiary, marginBottom: 4 },
  verifyLink: {
    color: theme.app.green, fontWeight: 700, fontSize: 13.5, cursor: "pointer",
    display: "inline-flex", alignItems: "center", gap: 4,
  },
}));

const NONE = "";

// entrada: só fluxo de chamada recebida (ou legado sem tipo) e agente — fora
// ferramenta, coletor, webhook etc. (não atendem chamada)
const FOR_INBOUND = [null, undefined, "", "inbound_call", "agent"];
const eligibleInbound = apps =>
  apps.filter(
    a => (a.triggerType === "agent" || a.trigger === "inbound" || a.trigger === "both") && FOR_INBOUND.includes(a.triggerType)
  );

const digits = v => String(v || "").replace(/\D/g, "");

const NumberLinkDialog = ({ open, number, applications, onClose, onSaved, testStatus, agents = [], onTestBind }) => {
  const classes = useStyles();
  const history = useHistory();
  const [inbound, setInbound] = useState(NONE);
  const [agent, setAgent] = useState(NONE);
  const [saving, setSaving] = useState(false);

  // é o número de TESTE compartilhado? (linha única, config especial na entrada)
  const isTest = !!(testStatus && number && digits(number.number) === digits(testStatus.number));
  const verified = !!(testStatus && testStatus.hasVerified);

  useEffect(() => {
    if (!number) return;
    setInbound(number.inboundApplication ? number.inboundApplication.id : NONE);
    setAgent(testStatus && testStatus.binding ? testStatus.binding.applicationId : NONE);
  }, [number, testStatus]);

  const handleSave = async () => {
    setSaving(true);
    try {
      if (isTest) {
        // número de teste: a "entrada" é a amarração do agente (não um app do DID).
        // Saída não é configurável aqui (é por-originação: fluxo/agente + limite do plano).
        await onTestBind(agent === NONE ? null : Number(agent));
        onClose();
        return;
      }
      const { data } = await api.put(`/numbers/${number.id}`, {
        inboundApplicationId: inbound === NONE ? null : inbound,
      });
      onSaved(data);
      onClose();
    } catch (err) {
      toastError(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{i18n.t("numbers.link")}</DialogTitle>
      <DialogContent>
        {number && (
          <Typography className={classes.number}>{formatPhone(number.number)}</Typography>
        )}

        {/* ENTRADA */}
        {isTest && !verified ? (
          <div className={classes.verifyField}>
            <div className={classes.verifyLabel}>{i18n.t("numbers.inbound")}</div>
            <Tooltip
              arrow
              title="Para receber chamadas você precisa verificar o seu número — como o número de teste é compartilhado, é assim que ele reconhece você."
            >
              <span className={classes.verifyLink} onClick={() => history.push("/settings")}>
                Verificar meu número →
              </span>
            </Tooltip>
          </div>
        ) : isTest ? (
          <TextField
            className={classes.field}
            select
            fullWidth
            variant="outlined"
            size="small"
            label={i18n.t("numbers.inbound")}
            value={agent}
            onChange={e => setAgent(e.target.value)}
          >
            <MenuItem value={NONE}>{i18n.t("numbers.none")}</MenuItem>
            {agents.map(a => (
              <MenuItem key={a.id} value={a.id}>{a.name || `Agente ${a.id}`}</MenuItem>
            ))}
          </TextField>
        ) : (
          <TextField
            className={classes.field}
            select
            fullWidth
            variant="outlined"
            size="small"
            label={i18n.t("numbers.inbound")}
            value={inbound}
            onChange={e => setInbound(e.target.value)}
          >
            <MenuItem value={NONE}>{i18n.t("numbers.none")}</MenuItem>
            {eligibleInbound(applications).map(a => (
              <MenuItem key={a.id} value={a.id}>
                {a.name}
              </MenuItem>
            ))}
          </TextField>
        )}

        <div className={classes.hint}>
          {isTest
            ? "O número de teste é compartilhado: para receber, ele usa o seu número verificado."
            : i18n.t("numbers.linkHint")}
        </div>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{i18n.t("apps.close")}</Button>
        <Button
          variant="contained"
          color="secondary"
          onClick={handleSave}
          disabled={saving}
        >
          {i18n.t("config.save")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default NumberLinkDialog;
